import { enqueueAlert } from "./alertSlice";
// Import the necessary functions from 'graphql-relay'
import { toGlobalId, fromGlobalId } from "graphql-relay";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { commonLinks, linkWithLanguage } from "components/helpers/commonLinks";
import i18n from "config/i18n";
import { syncLoginSessions } from "components/helpers/helper_functions";
import { removeAllPrivatePricing } from "components/programs/programDetails/programDetailsSlice";

interface CreateQueryProps {
  filter: any;
  customFields?: {
    customKey: string;
    newValue: string;
    valueType: "text" | "array";
    secondaryValue?: string;
  }[];
}

export const createQuery = ({ filter, customFields }: CreateQueryProps) => {
  const customFieldsObj = Object.fromEntries(
    customFields?.map((item) => [item.customKey, item]) || []
  );

  return Object.entries(filter)
    .filter(([key, value]) => value)
    .map(([key, value]) => {
      const customObj = customFieldsObj[key];

      if (customObj?.newValue && value) {
        const { newValue, valueType, secondaryValue } = customObj;

        if (valueType === "array" && Array.isArray(value)) {
          return value.map((val) => `${newValue}=${val}`).join("&");
        } else {
          const param = `${newValue}=${value}`;

          if (secondaryValue) {
            return `${param}&${secondaryValue}=${value}`;
          } else {
            return param;
          }
        }
      } else {
        return `${key}=${value}`;
      }
    })
    .join("&");
};

const ALLAUTH_ERROR_I18N: Record<string, string> = {
  too_many_login_attempts: "auth.tooManyLoginAttempts",
  cannot_delete_email: "auth.cannotDeleteEmail",
  cannot_remove_primary_email: "auth.cannotRemovePrimaryEmail",
  cannot_set_primary_email: "auth.cannotSetPrimaryEmail",
  email_taken: "auth.emailTaken",
  duplicate_email: "auth.duplicateEmail",
  unverified_primary_email: "auth.unverifiedPrimaryEmail",
  unknown_email: "auth.unknownEmail",
  max_email_addresses: "auth.maxEmailAddresses",
  incorrect_code: "auth.incorrectCode",
};

const allauthErrorText = (item: any): string | any => {
  const i18nKey = item?.code ? ALLAUTH_ERROR_I18N[item.code] : undefined;
  if (i18nKey) {
    return i18n.t(i18nKey);
  }
  if (typeof item?.message === "string") {
    return item.message;
  }
  return item;
};

export const mapAllauthError = (error: any): string => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 429) {
    return i18n.t("auth.tooManyLoginAttempts");
  }
  if (status >= 500 || typeof data === "string") {
    return i18n.t("auth.genericError");
  }
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map(allauthErrorText).join(", ");
  }
  if (data?.data?.flows) {
    const requireEmailVerification = data.data.flows.some(
      (flow: { id: string }) => flow.id === "verify_email"
    );
    if (requireEmailVerification) {
      return i18n.t("auth.requireEmailVerification");
    }
  }
  return error?.message || i18n.t("auth.genericError");
};

export const apiErrorCatcher = (error: any, dispatch: any) => {
  let eResponse, eData;

  if (Array.isArray(error)) {
    // this is specific to graphql. error [] will have objects with messages
    eData = error[0].messages;
  } else {
    eResponse = error?.response;
    eData = eResponse?.data;
  }

  // this section is for supporting new django allauthAPI
  const isAllauth = error?.config?.url?.includes("allauth");

  if (isAllauth) {
    dispatch(
      enqueueAlert({ severity: "error", message: mapAllauthError(error) })
    );
    return;
  }

  const errorMessages = [];

  if (Array.isArray(eData)) {
    for (const key in eData) {
      const value = eData[key];

      if (Array.isArray(value)) {
        errorMessages.push(...value);
      } else if (typeof value === "object" && value !== null) {
        // If the value is an object (and not null), handle it accordingly
        // Assuming you want to concatenate all object values into errorMessages
        for (const innerKey in value) {
          if (Array.isArray(value[innerKey])) {
            // If the inner value is an array, spread its items into errorMessages
            errorMessages.push(...value[innerKey]);
          } else {
            // If the inner value is not an array, just push the value
            errorMessages.push(value[innerKey]);
          }
        }
      } else {
        errorMessages.push(value);
      }
    }
  } else {
    for (const key in eData) {
      const value = eData[key];
      errorMessages.push(value);
    }
  }

  let errorMessage = error.message;

  if (errorMessages.length > 0) {
    errorMessage = errorMessages.join(", ");
  }

  dispatch(enqueueAlert({ severity: "error", message: errorMessage }));
};

// Function to convert a number to a global identifier
export const numberToGlobalId = (type: string, number: number) => {
  // Use the toGlobalId function to create a global identifier
  return toGlobalId(type, number.toString());
};

// Function to convert a global identifier back to a number
export const globalIdToNumber = (globalId: string) => {
  // Use the fromGlobalId function to parse the global identifier
  const { id } = fromGlobalId(globalId);
  // Assuming that the ID stored in the global identifier is a number
  // You may need to add validation or handle different types accordingly
  return parseInt(id, 10);
};

export const handleFileDownload = (response: any) => {
  // Create a Blob object from the binary data
  const blob = new Blob([response.data], {
    type: response.headers["content-type"],
  });

  // Create a download link
  const downloadLink = document.createElement("a");
  downloadLink.href = window.URL.createObjectURL(blob);
  // downloadLink.download = "exported_data.zip"; // Specify the filename you want

  // Append the download link to the body
  document.body.appendChild(downloadLink);

  // Trigger the download
  downloadLink.click();

  // Remove the download link from the body
  document.body.removeChild(downloadLink);
};

export const setSessionToken = (sessionToken: string) => {
  Cookies.set("sessionToken", sessionToken);
};

export const setAuthTokens = (
  access: string,
  refresh: string,
  sessionToken = null
) => {
  const { exp: newAccessTokenExp } = jwtDecode(access);
  const { exp: newRefreshTokenExp } = jwtDecode(refresh);

  Cookies.set("accessToken", access, {
    // @ts-ignore
    expires: new Date(newAccessTokenExp * 1000),
  });
  Cookies.set("refreshToken", refresh, {
    // @ts-ignore
    expires: new Date(newRefreshTokenExp * 1000),
  });

  if (sessionToken) {
    // sessionToken is used only on Allauth related api
    setSessionToken(sessionToken);
  }
};

export const deleteAllCookies = () => {
  const allCookies = Cookies.get(); // Get all cookies

  for (const cookie in allCookies) {
    // exlucde some cookies to be deleted
    if (!["download_app_popup_viewed"].includes(cookie)) {
      // Delete each cookie
      Cookies.remove(cookie);
    }
  }
};

export const logoutButton = (redirectUrl: string = "") => {
  // Lazy import avoids a circular store <-> slice load that left
  // rootApplicantSlice undefined during configureStore.
  const { default: store, persistor } = require("redux/index");
  // Purge is required to clear user object that is persisted on redux
  persistor.purge();
  syncLoginSessions();
  // deleting cookies for related tokens
  deleteAllCookies();
  // deleting localstorage for any save data
  localStorage.clear();
  sessionStorage.clear();
  store.dispatch(removeAllPrivatePricing());
  if (!redirectUrl) {
    redirectUrl = commonLinks.homeLink;
  }
  window.location.replace(linkWithLanguage(redirectUrl));
};
